import React, {
  useState, useEffect, useCallback, useRef, useMemo, Fragment, RefObject, ReactElement,
} from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import DraggableItem from '../../../components/draggable-item/draggable-item.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../components/padded-flex-container/padded-flex-container.component';
import LongClickListItem from '../../../components/long-click-list-item/long-click-list-item.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import FilteredCell from '../../../components/filtered-cell/filtered-cell.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableResize from '../../../components/table-resize/table-resize.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import List from '../../../components/list/list.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import TableSortLabel from '../../../components/table-sort-label/table-sort-label.component';
import TablePagination from '../../../components/table-pagination/table-pagination.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ActionButton from '../../../components/action-button/action-button.component';
import ListItemImage from '../../../components/list-item-image/list-item-image.component';
import ListItemCheckbox from '../../../components/list-item-checkbox/list-item-checkbox.component';
import ListItemSummary from '../../../components/list-item-summary/list-item-summary.component';
import VisibleCell from '../../../components/visible-cell/visible-cell.component';
import IconTextBlock from '../../../components/icon-text-block/icon-text-block.component';
import TableRowSummary from '../../../components/table-row-summary/table-row-summary.component';
import LastUpdateBlock from '../../app/common/last-update-block/last-update-block.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import ActivityRow from '../activity-row/activity-row.component';
import EditIcon from '../../../icons/edit-icon';
import ChangeStatusIcon from '../../../icons/change-status-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import OutboundIcon from '../../../icons/outbound-icon';
import InboundIcon from '../../../icons/inbound-icon';
import MoreIcon from '../../../icons/more-icon';
import AddCommentIcon from '../../../icons/add-comment-icon';
import ArrowLinkIcon from '../../../icons/arrow-link-icon';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import ScreenService from '../../../services/screen-service';
import useArrowNavigation from '../../../hooks/use-arrow-navigation';
import useScreenSmall from '../../../hooks/use-screen-small';
import { SORT_TYPE_ASC, SORT_TYPE_DESC } from '../../../constants/system/sort-types';
import {
  ACTIVITY_ATTACHMENT_LIST_VIEW, ACTIVITY_COMMENT_CREATE, ACTIVITY_COMMENT_LIST_VIEW, ACTIVITY_DELETE, ACTIVITY_EDIT,
  ACTIVITY_STATUS_EDIT, CONTACT_VIEW, DEAL_VIEW, SEARCH_RESULT_VIEW,
} from '../../../constants/entity/permissions';
import { ROUTE_ACTIVITY, ROUTE_CONTACT, ROUTE_DEAL } from '../../../constants/system/routes';
import { QUERY_PARAM_EDIT_MODE } from '../../../constants/system/query-params';
import { DATA_ACTIVITY_CATEGORY, DATA_ACTIVITY_ID } from '../../../constants/system/data-attributes';
import { SETTING_NAME_TABLE_COLUMN_SETTINGS } from '../../../constants/entity/setting-names';
import {
  ACTIVITY_CATEGORY_NEW_DEAL, ACTIVITY_CATEGORY_CAPITAL_RAISE,
} from '../../../constants/entity/activity-categories';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const ActivityListInternal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const HEADER_NAME_ACTIONS: string = 'actions';

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isExportMode,
    isSelectMode,
    isCustomizeMode,
    isDealActivity,
    isStatusFilterVisible,
    withAllRowsSelected,
    withAllFiltersEmpty,
    page,
    limit,
    sortParams,
    selectedRows,
    tableName,
    category,
    dealId,
    currentUser,
    activityActiveIds,
    inlineSaveActivityId,
    activityListData,
    activityTotal,
    activityListSummaryData,
    tableColumnSettings,
    activityTypeList,
    activityStatusList,
    activityCategoryList,
    activityInterestLevelList,
    dealList,
    contactList,
    contactCompanyList,
    contactPersonList,
    contactPersonInternalList,
    measureUnitList,
    tagList,
    onTagsChange,
    onTagClick,
    onInlineSave,
    onInlineCancel,
    onLongClick,
    onOpenModal,
    onOpenDeleteConfirmModal,
    onSelectRow,
    onPageChange,
    onLimitChange,
    onSortParamsChange,
    onTableColumnsChange,
    onDealContactsChange,
    onOpenStatusChangeModal,
    onInlineEditModeButtonClick,
    onActivityDealSearch,
    onActivityDealSearchClose,
    onContactSearch,
    onContactSearchClose,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactPersonInternalSearch,
    onContactPersonInternalSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
  } = props;

  const isSmallScreen: boolean = useScreenSmall();
  const navigate: ReactRouterNavigate = useNavigate();
  const tableRef: RefObject<HTMLTableElement> | null = useRef(null);
  const headerCellRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [extendedTableFields, setExtendedTableFields]: [TableField[] | null, Function]
    = useState<StateValueDefault>(null);

  const ACTIVITY_CATEGORY_ICON_MAP: Record<string, ReactElement> = useMemo((): Record<string, ReactElement> => ({
    [ACTIVITY_CATEGORY_NEW_DEAL]: <InboundIcon />,
    [ACTIVITY_CATEGORY_CAPITAL_RAISE]: <OutboundIcon />,
  }), []);

  const activityCommentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_LIST_VIEW], []);
  const activityAttachmentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_ATTACHMENT_LIST_VIEW], []);
  const activityEditPermissions: string[] = useMemo((): string[] => [ACTIVITY_EDIT], []);

  const tableColumnDropDeps: DepsItem[] = useMemo((): DepsItem[] => [
    isCustomizeMode, tableColumnSettings,
  ], [isCustomizeMode, tableColumnSettings]);

  const withFilters: boolean = !withAllFiltersEmpty && !isEditMode && !isExportMode && !isSelectMode
    && !isCustomizeMode;
  const withButtons: boolean = isEditMode || isExportMode;
  const withPagination: boolean = !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode;
  const withStatusFilter: boolean = isStatusFilterVisible && !isDealActivity && !isSmallScreen;
  const withTabs: boolean = isDealActivity;

  const checkIsActivityActive: Function = useCallback((activityId: number): boolean => (
    activityActiveIds ? activityActiveIds.includes(activityId) : false
  ), [activityActiveIds]);

  const openModal: Function = useCallback((activityItem: Activity, isCommentActive?: boolean): void => {
    if (onOpenModal) {
      onOpenModal(activityItem, isCommentActive);
    }
  }, []);

  const changePage: ChangePageHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent> | null, pageValue: number,
  ): void => {
    if (onPageChange) {
      onPageChange(pageValue);
    }

    ScreenService.resetScrollPosition();
  }, [page, limit]);

  const changeLimit: ChangeLimitHandlerFunction = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    if (onLimitChange && event.target) {
      onLimitChange(event.target.value);
    }
  }, [page, limit]);

  const openDeleteConfirmModal: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    activityId: number,
  ): void => {
    if (onOpenDeleteConfirmModal) {
      onOpenDeleteConfirmModal(event, activityId);
    }
  }, []);

  const onLastUpdateColumnClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityItem: Activity,
  ): void => {
    if (activityItem) {
      openModal(activityItem);
    }

    event.stopPropagation();
  }, []);

  const onDealClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    dealItemId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_DEAL}/${dealItemId}`,
    });

    event.stopPropagation();
  }, []);

  const onContactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactId: number,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactId}`,
    });

    event.stopPropagation();
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <AddCommentIcon />,
      text: 'Add Comment',
      permission: ACTIVITY_COMMENT_CREATE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, activityId: number): void => {
        if (activityListData) {
          activityListData.forEach((activityItem: Activity): void => {
            if (activityItem.id === activityId) {
              openModal(activityItem, true);
            }
          });
        }
      },
    },
    {
      id: 2,
      icon: <ChangeStatusIcon />,
      text: 'Change Status',
      permission: ACTIVITY_STATUS_EDIT,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, activityId: number): void => {
        if (onOpenStatusChangeModal) {
          onOpenStatusChangeModal(event, [activityId]);
        }
      },
    },
    {
      id: 3,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: ACTIVITY_DELETE,
      isVisibleByEntityTypeValue: ACTIVITY_CATEGORY_CAPITAL_RAISE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, activityId: number): void => {
        if (activityId) {
          openDeleteConfirmModal(event, activityId);
        }
      },
    },
  ], [activityListData]);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const tagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    tagId: number,
  ): void => {
    if (onTagClick) {
      onTagClick(event, tagId);
    }

    event.stopPropagation();
  }, []);

  const contactClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contactItem: Contact,
  ): void => {
    navigate({
      pathname: `${ROUTE_CONTACT}/${contactItem.id}`,
    });

    event.stopPropagation();
  }, []);

  const longClick: Function = useCallback((activityId: number): void => {
    if (!isEditMode && !isSelectMode && !isExportMode && !isCustomizeMode) {
      if (onLongClick) {
        onLongClick();
      }

      onSelectRow(activityId);
    }
  }, [isExportMode, isSelectMode, isEditMode, isCustomizeMode]);

  const onActionCellClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const tableActivityListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'category',
      title: 'Category',
      isSortable: false,
      isNumber: false,
      isHidden: !!category,
      component: (activity: Activity): ReactElement | boolean => {
        const activityCategory: ActivityCategory | null = activityCategoryList && activityCategoryList.length
          ? activityCategoryList.find((activityCategoryItem: ActivityCategory): boolean => (
            activityCategoryItem.name === activity.category
          )) : null;
        const activityCategoryIcon: ReactElement | undefined = (activityCategory
          && ACTIVITY_CATEGORY_ICON_MAP[activityCategory.name]) || undefined;

        return (
          !category && !!activityCategory && (
            <TableCell>
              <IconTextBlock
                icon={(
                  !!activityCategoryIcon && (
                    <Tooltip title={activityCategory.title}>
                      {activityCategoryIcon}
                    </Tooltip>
                  )
                )}
              >
                {activityCategory.title}
              </IconTextBlock>
            </TableCell>
          )
        );
      },
      summaryComponent: (): ReactElement => (
        !category ? (
          <TableCell isSticky={!isCustomizeMode} isStickyLeft={!isCustomizeMode}>
            Total
          </TableCell>
        ) : (
          <TableCell />
        )
      ),
    },
    {
      id: 2,
      name: 'status',
      title: 'Status',
      isSortable: true,
      isNumber: false,
      isRequired: isEditMode,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {activity.statusText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => (
        category ? (
          <TableCell isSticky={!isCustomizeMode} isStickyLeft={!isCustomizeMode}>
            Total
          </TableCell>
        ) : (
          <TableCell />
        )
      ),
    },
    {
      id: 3,
      name: 'type',
      title: 'Type',
      isSortable: true,
      isNumber: false,
      isRequired: isEditMode,
      component: (activity: Activity): ReactElement => (
        <TableCell>
          {activity.typeText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 4,
      name: 'deal.name',
      title: 'Deal',
      isRequired: isEditMode,
      isSortable: true,
      isNumber: false,
      isVisibleByEntity: true,
      isSticky: true,
      component: (activity: Activity): ReactElement | boolean => (
        !dealId && (
          <TableCell
            isText
            isSticky={!isCustomizeMode}
            isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode}
            isStickyLeftSelect={!isCustomizeMode && (isExportMode || isSelectMode)}
          >
            {
              activity.deal && (
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  label={activity.deal.name}
                  imageLink={activity.deal.mainPhotoThumbnail || activity.deal.mainPhoto || AssetDefaultImage}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onDealClick(event, activity.deal.id);
                  }}
                  permission={DEAL_VIEW}
                />
              )
            }
          </TableCell>
        )
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 5,
      name: 'dealSource.name',
      title: 'Deal Source',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.dealSource && (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                label={activity.dealSource.name}
                imageLink={activity.dealSource.photoThumbnail || activity.dealSource.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, activity.dealSource.id);
                }}
              />
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 6,
      name: 'sponsor.fullName',
      title: 'Sponsor',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.sponsor && (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                label={activity.sponsor.fullName}
                imageLink={activity.sponsor.photoThumbnail || activity.sponsor.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, activity.sponsor.id);
                }}
              />
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 7,
      name: 'broker.fullName',
      title: 'Broker',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.broker && (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                label={activity.broker.fullName}
                imageLink={activity.broker.photoThumbnail || activity.broker.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, activity.broker.id);
                }}
              />
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 8,
      name: 'lender.fullName',
      title: 'Lender',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.lender && (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                label={activity.lender.fullName}
                imageLink={activity.lender.photoThumbnail || activity.lender.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, activity.lender.id);
                }}
              />
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 9,
      name: 'amount',
      title: 'Total Capitalization',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.amount}
        </TableCell>
      ),
      summaryComponent: (activitySummaryData: Activity): ReactElement => (
        <TableCell isNumber>
          {activitySummaryData.amount}
        </TableCell>
      ),
    },
    {
      id: 10,
      name: 'pricing',
      title: 'Price Guidance',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {activity.pricing}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 11,
      name: 'size',
      title: 'Size',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.size && activity.sizeType && `${activity.size} ${activity.sizeType && activity.sizeType.title}`}
        </TableCell>
      ),
      summaryComponent: (activitySummaryData: Activity): ReactElement => (
        <TableCell isNumber>
          {activitySummaryData.size && activitySummaryData.sizeType && activitySummaryData.sizeType.title
            && `${activitySummaryData.size} ${activitySummaryData.sizeType.title}`}
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'dealContacts.fullName',
      title: 'Deal Contacts',
      isSortable: false,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.dealContacts && !!activity.dealContacts.length
            && activity.dealContacts.map((contactItem: Contact): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={contactItem.id}
                label={contactItem.fullName}
                imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  contactClick(event, contactItem);
                }}
              />
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 13,
      name: 'dealContacts.phone',
      title: 'Deal Contact Phones',
      isSortable: false,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.dealContacts && !!activity.dealContacts.length
            && activity.dealContacts.map((contactItem: Contact): ReactElement | boolean => (
              !!contactItem.phone && !!contactItem.phoneText && (
                <FilteredLink
                  isPhone
                  key={contactItem.id}
                  link={!isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                    ? FormatService.formatCleanIntegerNumberString(contactItem.phone) : null}
                >
                  {contactItem.phoneText}
                </FilteredLink>
              )
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 14,
      name: 'dealContacts.email',
      title: 'Deal Contact Emails',
      isSortable: false,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.dealContacts && !!activity.dealContacts.length
            && activity.dealContacts.map((contactItem: Contact): ReactElement | boolean => (
              !!contactItem.email && (
                <FilteredLink
                  isEmail
                  key={contactItem.id}
                  link={!isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode ? contactItem.email : null}
                >
                  {contactItem.email}
                </FilteredLink>
              )
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 15,
      name: 'purchasePrice',
      title: 'Purchase Price',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.purchasePrice}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 16,
      name: 'capitalExpenditure',
      title: 'CapEx',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.capitalExpenditure}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 17,
      name: 'oneYearNoi',
      title: '1 Year NOI',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.oneYearNoi}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 18,
      name: 'yieldOnCost',
      title: 'Yield On Cost',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.yieldOnCost}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 19,
      name: 'debtProceeds',
      title: 'Debt Proceeds',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.debtProceeds}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 20,
      name: 'requiredEquityAmount',
      title: 'Required Equity',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.requiredEquityAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 21,
      name: 'interestLevel',
      title: 'Level of Interest',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell>
          {activity.interestLevelText}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 22,
      name: 'ltc',
      title: 'LTC',
      isSortable: true,
      isNumber: true,
      component: (activity: Activity): ReactElement => (
        <TableCell isNumber>
          {activity.ltc}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 23,
      name: 'equityProvider.fullName',
      title: 'Equity Provider',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.equityProvider && (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                label={activity.equityProvider.fullName}
                imageLink={activity.equityProvider.photoThumbnail || activity.equityProvider.photo
                  || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent): void => {
                  onContactClick(event, activity.equityProvider.id);
                }}
              />
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 24,
      name: 'investor.fullName',
      title: 'Investor',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.investor && (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                label={activity.investor.fullName}
                imageLink={activity.investor.photoThumbnail || activity.investor.photo || CompanyDefaultImage}
                permission={CONTACT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  onContactClick(event, activity.investor.id);
                }}
              />
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 25,
      name: 'lenderRole',
      title: 'Lender Role',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {activity.lenderRole}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 26,
      name: 'maxAmount',
      title: 'Max Amount',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {activity.maxAmount}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 27,
      name: 'loanRecourse',
      title: 'Loan Recourse',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {activity.loanRecourse}
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 28,
      name: 'additionalParty.fullName',
      title: 'Additional Party',
      isSortable: true,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.additionalParty && (
              <FlexContainer>
                <ChipLink
                  isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                  label={activity.additionalParty.fullName}
                  imageLink={activity.additionalParty.photoThumbnail || activity.additionalParty.photo
                    || CompanyDefaultImage}
                  permission={CONTACT_VIEW}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onContactClick(event, activity.additionalParty.id);
                  }}
                />
                {
                  activity.additionalPartyDetails && (
                    <PaddedFlexContainer>
                      {activity.additionalPartyDetails}
                    </PaddedFlexContainer>
                  )
                }
              </FlexContainer>
            )
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 29,
      name: 'updatedDate',
      title: 'Last Update',
      isSortable: true,
      isNumber: true,
      alignLeft: true,
      component: (activity: Activity): ReactElement => (
        <TableCell
          isText
          isTextNormal
          onClick={(
            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
              ? ((event: React.MouseEvent<Element, MouseEvent>): void => {
                onLastUpdateColumnClick(event, activity);
              }) : null
          )}
        >
          <LastUpdateBlock
            isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
            userText={activity.updatedByUserText}
            dateText={activity.updatedDateText}
            commentList={activity.comments}
            attachmentList={activity.attachments}
            commentListViewPermissions={activityCommentListViewPermissions}
            attachmentListViewPermissions={activityAttachmentListViewPermissions}
          />
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 30,
      name: 'tags',
      title: 'Tags',
      isSortable: false,
      isNumber: false,
      component: (activity: Activity): ReactElement => (
        <TableCell isText>
          {
            activity.tags && !!activity.tags.length && activity.tags.map((tagItem: Tag): ReactElement => (
              <ChipLink
                isDisabled={isEditMode || isExportMode || isSelectMode || isCustomizeMode}
                key={tagItem.id}
                label={tagItem.name}
                permission={SEARCH_RESULT_VIEW}
                onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                  tagClick(event, tagItem.id);
                }}
              />
            ))
          }
        </TableCell>
      ),
      summaryComponent: (): ReactElement => <TableCell />,
    },
    {
      id: 31,
      name: 'actions',
      title: 'Actions',
      isSortable: false,
      isNumber: false,
      isHeaderNameHidden: true,
      isNotFiltered: true,
      isSticky: !isCustomizeMode && !isExportMode && !isSelectMode,
      isStickyRight: !isCustomizeMode && !isExportMode && !isSelectMode,
      component: (activity: Activity): ReactElement => (
        <TableCell
          isActions
          isTextRight
          isSticky={!isCustomizeMode && !isExportMode && !isSelectMode}
          isStickyRight={!isCustomizeMode && !isExportMode && !isSelectMode}
          onClick={onActionCellClick}
        >
          {
            !isExportMode && !isSelectMode && !isCustomizeMode
            && (!isEditMode || !checkIsActivityActive(activity.id)) && (
              <CheckAccess permissions={activityEditPermissions}>
                <ActionButton
                  withNoPadding
                  withFixedSize
                  isHidden={!!isSaving}
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onInlineEditModeButtonClick(event, activity.id);
                  }}
                >
                  <EditIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
          {
            !isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && (
              <CheckAccess
                permissions={actionMenuItems
                  && actionMenuItems.map((actionMenuItem: ActionMenuItem): string | undefined => (
                    actionMenuItem.permission
                  ))}
              >
                <ActionButton
                  withPopup
                  withNoPadding
                  withFixedSize
                  isRight={!activity.comments.length}
                  isHidden={!!isSaving}
                  onClick={openActionMenu}
                  {...{
                    [DATA_ACTIVITY_ID]: activity.id,
                    [DATA_ACTIVITY_CATEGORY]: activity.category,
                  }}
                >
                  <MoreIcon />
                </ActionButton>
              </CheckAccess>
            )
          }
        </TableCell>
      ),
      summaryComponent: (activitySummaryData: Activity, fieldId: number): ReactElement => (
        <TableCell key={fieldId} />
      ),
    },
  ], [isCustomizeMode, isEditMode, isExportMode, isSelectMode, activityActiveIds, isSaving, category]);

  const onCheckIsMenuItemVisible: Function = useCallback((
    actionMenuItem: ActionMenuItem,
    activityCategory: string,
  ): boolean => (
    (actionMenuItem.isVisibleByEntityTypeValue && activityCategory
      && actionMenuItem.isVisibleByEntityTypeValue === activityCategory)
    || !actionMenuItem.isVisibleByEntityTypeValue
  ), []);

  const sortTableRows: Function = useCallback((columnName: string): void => {
    if (onSortParamsChange) {
      onSortParamsChange(
        columnName,
        sortParams.field === columnName && sortParams.type === SORT_TYPE_DESC ? SORT_TYPE_ASC : SORT_TYPE_DESC,
      );
    }
  }, [sortParams]);

  const selectRow: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    rowId: number,
  ): void => {
    if (onSelectRow) {
      onSelectRow(rowId);
    }

    event.stopPropagation();
  }, [selectedRows]);

  const checkIsRowSelected: Function = useCallback((rowId: number): boolean => (
    selectedRows ? selectedRows.includes(rowId) : false
  ), [selectedRows]);

  const onRowClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityId: number, isListView?: boolean,
  ): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection && !isCustomizeMode) {
      if (isExportMode || isSelectMode) {
        selectRow(event, activityId);
      } else if (isEditMode && isListView) {
        const queryParams: LocationQueryParams = {
          [QUERY_PARAM_EDIT_MODE]: isEditMode,
        };

        navigate({
          pathname: `${ROUTE_ACTIVITY}/${activityId}`,
          search: FormatService.formatQueryParams(queryParams),
        });
      } else if (!isEditMode) {
        navigate({
          pathname: `${ROUTE_ACTIVITY}/${activityId}`,
        });
      }
    }
  }, [selectedRows, isCustomizeMode, isExportMode, isSelectMode, isEditMode]);

  const onTableColumnSettingsChange: Function = useCallback((
    updatedTableColumnSettings: TableField[],
  ): void => {
    if (updatedTableColumnSettings && updatedTableColumnSettings.length) {
      if (onTableColumnsChange) {
        onTableColumnsChange(updatedTableColumnSettings);
      }

      setExtendedTableFields(
        StructureService.extendOptions(
          tableActivityListFields.filter((tableField: TableField): boolean => !tableField.isEditModeOnly),
          updatedTableColumnSettings,
        ),
      );
    }
  }, [tableActivityListFields]);

  const onDropColumn: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    if (tableColumnSettings) {
      onTableColumnSettingsChange(StructureService.switchListItems(tableColumnSettings, dragIndex, hoverIndex));
    }
  }, [extendedTableFields, tableColumnSettings, ...tableColumnDropDeps]);

  useEffect((): void => {
    if (currentUser && !tableColumnSettings) {
      const tableColumnSettingsCommon: Setting = currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_TABLE_COLUMN_SETTINGS
        ));

      if (onTableColumnsChange) {
        onTableColumnsChange(
          tableColumnSettingsCommon && tableColumnSettingsCommon.value && tableColumnSettingsCommon.value[tableName],
          true,
        );
      }
    }
  }, [currentUser]);

  useEffect((): void => {
    if (tableColumnSettings && tableColumnSettings.length && !extendedTableFields) {
      setExtendedTableFields(StructureService.extendOptions(tableActivityListFields, tableColumnSettings));
    }
  }, [tableColumnSettings]);

  useEffect((): void => {
    if (extendedTableFields) {
      setExtendedTableFields(StructureService.extendOptions(tableActivityListFields, tableColumnSettings, isEditMode));
    }
  }, [isEditMode, isExportMode, isSelectMode, isCustomizeMode, activityActiveIds, isSaving]);

  useArrowNavigation(inputRefs);

  return (
    <Fragment>
      {
        activityListData && !!activityListData.length && extendedTableFields && !!extendedTableFields.length ? (
          <Fragment>
            <Card>
              {
                !isSmallScreen ? (
                  <ContentListContainerOffset
                    withFilters={withFilters}
                    withButtons={withButtons}
                    withPagination={withPagination}
                    withStatusFilter={withStatusFilter}
                    withTabs={withTabs}
                  >
                    <TableResize
                      isActive={isCustomizeMode}
                      isDisabled={isEditMode}
                      tableRef={tableRef}
                      headerCellRefs={headerCellRefs}
                      tableColumnSettings={tableColumnSettings}
                      onTableColumnsChange={onTableColumnSettingsChange}
                    >
                      <Table withStickyHeader isFullWidth={isEditMode} ref={tableRef}>
                        <TableHead isHeader>
                          <TableRow>
                            {
                              (isExportMode || isSelectMode) && (
                                <TableCell isSticky isStickyLeft />
                              )
                            }
                            {
                              extendedTableFields.map((
                                tableField: TableField, tableFieldIndex: number,
                              ): ReactElement => (
                                <VisibleCell
                                  isEditMode={isEditMode}
                                  withEntity={!dealId}
                                  key={tableField.id}
                                  tableField={tableField}
                                >
                                  <FilteredCell
                                    columnName={tableField.name}
                                    tableName={tableName}
                                    currentUser={currentUser}
                                  >
                                    <TableCell
                                      isActions={tableField.name === HEADER_NAME_ACTIONS}
                                      isNumber={tableField.isNumber && !tableField.alignLeft && !isEditMode}
                                      isTextRight={tableField.name === HEADER_NAME_ACTIONS}
                                      isSticky={!isCustomizeMode && tableField.isSticky}
                                      isStickyLeft={!isCustomizeMode && !isExportMode && !isSelectMode
                                        && tableField.isSticky && !tableField.isStickyRight}
                                      isStickyLeftSelect={!isCustomizeMode && (isExportMode || isSelectMode)
                                        && tableField.isSticky && !tableField.isStickyRight}
                                      isStickyLeftHeader={!isCustomizeMode && !isExportMode && !isSelectMode
                                        && tableField.isSticky && !tableField.isStickyRight}
                                      isStickyRight={!isCustomizeMode && tableField.isSticky
                                        && tableField.isStickyRight}
                                      isStickyRightHeader={!isCustomizeMode && !isExportMode && !isSelectMode
                                        && tableField.isSticky && tableField.isStickyRight}
                                      ref={(ref: HTMLElement): void => {
                                        if (headerCellRefs.current) {
                                          headerCellRefs.current[tableFieldIndex] = ref;
                                        }
                                      }}
                                      sortDirection={sortParams.field === tableField.name
                                      && tableField.isSortable ? sortParams.type : false}
                                    >
                                      {
                                        tableField.isSortable && !isEditMode ? (
                                          <DraggableItem
                                            isDisabled={!isCustomizeMode}
                                            itemId={tableField.id}
                                            index={tableFieldIndex}
                                            dropDeps={tableColumnDropDeps}
                                            onDropItem={onDropColumn}
                                          >
                                            <TableSortLabel
                                              withNoLabel={tableField.isHeaderNameHidden}
                                              isActive={sortParams.field === tableField.name}
                                              sortType={sortParams.type}
                                              onClick={(): void => {
                                                sortTableRows(tableField.name);
                                              }}
                                            >
                                              {tableField.title}
                                            </TableSortLabel>
                                          </DraggableItem>
                                        ) : (
                                          !tableField.isHeaderNameHidden && (
                                            <DraggableItem
                                              isDisabled={!isCustomizeMode}
                                              itemId={tableField.id}
                                              index={tableFieldIndex}
                                              dropDeps={tableColumnDropDeps}
                                              onDropItem={onDropColumn}
                                            >
                                              {tableField.title}
                                              {tableField.isRequired && <RequiredMark />}
                                            </DraggableItem>
                                          )
                                        )
                                      }
                                    </TableCell>
                                  </FilteredCell>
                                </VisibleCell>
                              ))
                            }
                          </TableRow>
                        </TableHead>
                        <TableBody
                          isPlain
                          withZebraRows
                          withBorderedCells={!isCustomizeMode}
                          withFixedCellsSizeExtended={isEditMode}
                        >
                          {
                            activityListData && !!activityListData.length
                            && activityListData.map((activityItem: Activity, activityIndex: number): ReactElement => {
                              const isSelected: boolean = selectedRows
                                ? withAllRowsSelected || checkIsRowSelected(activityItem.id) : false;
                              const isActive: boolean = checkIsActivityActive(activityItem.id);
                              const isInlineSaving: boolean = inlineSaveActivityId !== null
                                ? activityItem.id === inlineSaveActivityId : isSaving;

                              return (
                                <ActivityRow
                                  isActive={isActive}
                                  isInlineSaving={isInlineSaving}
                                  isSaveDisabled={isSaveDisabled}
                                  isSaving={isSaving}
                                  isCustomizeMode={isCustomizeMode}
                                  isDealActivity={isDealActivity}
                                  isSelected={isSelected}
                                  isEditMode={isEditMode}
                                  isExportMode={isExportMode}
                                  isSelectMode={isSelectMode}
                                  key={activityItem.id}
                                  currentUser={currentUser}
                                  category={category}
                                  activityItem={activityItem}
                                  activityIndex={activityIndex}
                                  inputRefs={inputRefs}
                                  dealId={dealId}
                                  activityCategoryIconMap={ACTIVITY_CATEGORY_ICON_MAP}
                                  tableActivityListFields={extendedTableFields}
                                  tableColumnSettings={tableColumnSettings}
                                  tableName={tableName}
                                  dealList={dealList}
                                  tagList={tagList}
                                  measureUnitList={measureUnitList}
                                  activityTypeList={activityTypeList}
                                  activityInterestLevelList={activityInterestLevelList}
                                  activityStatusList={activityStatusList}
                                  activityCategoryList={activityCategoryList}
                                  contactList={contactList}
                                  contactCompanyList={contactCompanyList}
                                  contactPersonList={contactPersonList}
                                  contactPersonInternalList={contactPersonInternalList}
                                  onRowClick={onRowClick}
                                  onLongClick={longClick}
                                  onInlineSave={onInlineSave}
                                  onInlineCancel={onInlineCancel}
                                  onTagsChange={onTagsChange}
                                  onDealContactsChange={onDealContactsChange}
                                  onActivityDealSearch={onActivityDealSearch}
                                  onActivityDealSearchClose={onActivityDealSearchClose}
                                  onContactSearch={onContactSearch}
                                  onContactSearchClose={onContactSearchClose}
                                  onContactPersonSearch={onContactPersonSearch}
                                  onContactPersonSearchClose={onContactPersonSearchClose}
                                  onContactPersonInternalSearch={onContactPersonInternalSearch}
                                  onContactPersonInternalSearchClose={onContactPersonInternalSearchClose}
                                  onContactCompanySearch={onContactCompanySearch}
                                  onContactCompanySearchClose={onContactCompanySearchClose}
                                  onTagSearch={onTagSearch}
                                  onTagSearchClose={onTagSearchClose}
                                />
                              );
                            })
                          }
                          {
                            !isEditMode && !isExportMode && !isSelectMode && !isCustomizeMode
                            && activityListSummaryData && (
                              <TableRowSummary withNoHighlight>
                                {
                                  extendedTableFields.map((tableField: TableField): ReactElement => (
                                    <VisibleCell
                                      isEditMode={isEditMode}
                                      withEntity={!dealId}
                                      key={tableField.id}
                                      tableField={tableField}
                                    >
                                      {
                                        !!tableField.summaryComponent && (
                                          !tableField.isNotFiltered ? (
                                            <FilteredCell
                                              tableName={tableName}
                                              columnName={tableField.name}
                                              currentUser={currentUser}
                                            >
                                              {tableField.summaryComponent(activityListSummaryData)}
                                            </FilteredCell>
                                          ) : (
                                            tableField.summaryComponent(activityListSummaryData, tableField.id)
                                          )
                                        )
                                      }
                                    </VisibleCell>
                                  ))
                                }
                              </TableRowSummary>
                            )
                          }
                        </TableBody>
                      </Table>
                    </TableResize>
                  </ContentListContainerOffset>
                ) : (
                  <ContentListContainerOffset
                    isDefault
                    withFilters={withFilters}
                    withButtons={withButtons}
                    withPagination={withPagination}
                    withStatusFilter={withStatusFilter}
                    withTabs={withTabs}
                  >
                    <List>
                      {
                        activityListData.map((activityItem: Activity): ReactElement => {
                          const isSelected: boolean = selectedRows
                            ? withAllRowsSelected || checkIsRowSelected(activityItem.id) : false;

                          return (
                            <LongClickListItem
                              isButton={!isSelectMode && !isExportMode}
                              isActive={isSelected}
                              key={activityItem.id}
                              onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                onRowClick(event, activityItem.id, true);
                              }}
                              onLongClick={(): void => {
                                longClick(activityItem.id);
                              }}
                            >
                              {
                                (isExportMode || isSelectMode) && (
                                  <ListItemCheckbox isChecked={isSelected} itemId={activityItem.id} />
                                )
                              }
                              {
                                activityItem.deal && (
                                  <ListItemImage
                                    text={activityItem.deal && activityItem.deal.name}
                                    link={(activityItem.deal && (activityItem.deal.mainPhotoThumbnail
                                      || activityItem.deal.mainPhoto)) || AssetDefaultImage}
                                  />
                                )
                              }
                              <ListItemText
                                isBlock
                                subText={!isEditMode && activityItem.lender ? activityItem.lender.name : ''}
                              >
                                {activityItem.deal && activityItem.deal.name}
                              </ListItemText>
                              <ListItemText
                                isBlock
                                isBlockRight
                                subText={!isEditMode ? activityItem.statusText : (
                                  <FlexContainer isEnd>
                                    <ActionButton
                                      withSmallPadding
                                      onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                        onRowClick(event, activityItem.id, true);
                                      }}
                                    >
                                      <ArrowLinkIcon />
                                    </ActionButton>
                                  </FlexContainer>
                                )}
                              />
                            </LongClickListItem>
                          );
                        })
                      }
                      {
                        !isEditMode && !isExportMode && !isSelectMode && activityListSummaryData
                        && activityListSummaryData.amount && (
                          <ListItemSummary>
                            <ListItemText isBlock>
                              Total:
                            </ListItemText>
                            <ListItemText isBlock isBlockRight>
                              {activityListSummaryData.amount}
                            </ListItemText>
                          </ListItemSummary>
                        )
                      }
                    </List>
                  </ContentListContainerOffset>
                )
              }
            </Card>
            {
              !isExportMode && !isSelectMode && !isEditMode && !isCustomizeMode && (
                <TablePagination
                  page={page}
                  limit={limit}
                  totalItems={activityTotal}
                  onPageChange={changePage}
                  onLimitChange={changeLimit}
                />
              )
            }
          </Fragment>
        ) : (
          <NoContentBlock>
            {dealId ? 'No activities' : 'No activities found by your request'}
          </NoContentBlock>
        )
      }
      <ContextMenu
        menuAnchor={menuAnchor}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_ACTIVITY_ID}
        dataAttributeEntityType={DATA_ACTIVITY_CATEGORY}
        onClose={closeActionMenu}
        onCheckIsMenuItemVisible={onCheckIsMenuItemVisible}
      />
    </Fragment>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
  } = reducers;

  return {
    isExportMode: controls.isExportMode,
    isEditMode: controls.isEditMode,
    isCustomizeMode: controls.isCustomizeMode,
    isSelectMode: controls.isSelectMode,
    currentUser: app.baseData && app.baseData.currentUser,
  };
};

export default connect(mapState)(ActivityListInternal) as ReactComponent;
